import { Components as JoyComponents, Theme as JoyTheme } from '@mui/joy';
import JoyButton, {
  ButtonProps as JoyButtonProps,
  buttonClasses,
} from '@mui/joy/Button';
import { mergeProps } from '@react-aria/utils';
import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { z } from 'zod';

import { Link, LinkProps } from './link';

const SHAPE = ['pill', 'default'] as const;

declare module '@mui/joy/Button' {
  interface ButtonPropsColorOverrides {
    'gradient-primary': true;
    'gradient-secondary': true;
    white: true;
  }
}

const toCustomProps = (props: unknown) => {
  return z
    .object({
      shape: z.enum(SHAPE).catch('default'),
      inset: z.enum(['none', 'x', 'y', 'both']).catch('none'),
    })
    .parse(props);
};

export const overrides: JoyComponents<JoyTheme> = {
  JoyIconButton: {
    defaultProps: { size: 'md' },
    styleOverrides: {
      root: ({ ownerState }) => ({
        // Sets the default border radius for a "outline" button
        ...(ownerState.variant === 'outlined' && {
          '--variant-borderWidth': '2px',
        }),
      }),
    },
  },
  JoyButton: {
    defaultProps: { size: 'md' },
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        return {
          // White button variant colors
          ...(ownerState.color === 'white' && {
            '--variant-plainColor': theme.palette.common.white,
            '&:hover': {
              '--variant-plainColor': theme.palette.neutral[100],
            },
          }),

          ...(ownerState.size === 'sm' && {
            '--Button-paddingInline': '0.75rem',
            paddingInline: 'var(--Button-paddingInline, 0.75rem)',
          }),
          ...(ownerState.size === 'md' && {
            '--Button-paddingInline': '1rem',
            paddingInline: 'var(--Button-paddingInline, 1rem)',
          }),
          ...(ownerState.size === 'lg' && {
            '--Button-paddingInline': '1.5rem',
            paddingInline: 'var(--Button-paddingInline, 1.5rem)',
          }),

          ...(ownerState.variant === 'outlined' && {
            ...(ownerState.color === 'secondary' && {
              ':hover': {
                '--variant-outlinedColor': theme.palette.common.white,
              },
            }),
          }),

          //
          '--Button-radius': theme.radius.sm,
          ':active': {
            transform: 'scale(0.999)',
          },
          // '&:hover': {
          //   '--variant-outlinedColor': theme.palette.common.white,
          // },
          ...(ownerState.size === 'lg' && {
            '--Button-minHeight': theme.spacing(6),
          }),

          // Explicitly set the icon size for the button
          [`& .MuiSvgIcon-root, & .${buttonClasses.startDecorator} > svg, & .${buttonClasses.endDecorator} > svg`]:
            {
              margin: 'var(--Icon-margin)',
              fontSize: 'var(--Icon-fontSize, var(--joy-fontSize-xl2))',
              color:
                'var(--Icon-color, var(--joy-palette-text-icon, var(--joy-palette-neutral-500, #636B74)))',
            },
          // Sets the default transition for the button
          transitionProperty: 'color',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          transitionDuration: '150ms',

          // Sets the default border radius for the a "pill" button
          ...(toCustomProps(ownerState).shape === 'pill' && {
            '--Button-radius': 999,
            borderRadius: 999,
          }),

          // Sets the default border radius for a "outline" button
          ...(ownerState.variant === 'outlined' && {
            '--variant-borderWidth': '2px',
          }),

          // Disabled styles for gradient button
          ...((ownerState.color === 'gradient-primary' ||
            ownerState.color === 'gradient-secondary') &&
            !!ownerState.disabled && {
              backgroundColor: theme.palette.neutral[100],
              color: theme.palette.neutral[300],
            }),
          // Sets the styles for the "gradient primary" button
          ...(ownerState.color === 'gradient-primary' &&
            !ownerState.disabled && {
              '--Button-background': theme.palette.common.white,
              '--Button-text-color': theme.palette.common.white,
              color: 'var(--Button-text-color)',
              backgroundImage: theme.vars.palette.gradient.primary,
              backgroundSize: '100% 100%',
              backgroundPosition: '0% 0%',
              '&:hover': {
                backgroundSize: '400% 400%',
                backgroundPosition: '50% 40%',
              },

              // Outlined styles
              ...(ownerState.variant === 'outlined' && {
                '--Button-text-color': theme.palette.common.black,
                isolation: 'isolate',
                color: 'var(--Button-text-color)',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  zIndex: -10,
                  margin: 'var(--variant-borderWidth)',
                  borderRadius:
                    toCustomProps(ownerState).shape === 'pill'
                      ? 999
                      : 'calc(var(--Button-radius) - var(--variant-borderWidth))',
                  border: 'var(--variant-borderWidth) solid transparent',
                  backgroundColor: 'var(--Button-background)',
                },
                '&:hover::after': {
                  backgroundColor: 'transparent',
                },
                '&:hover': {
                  '--Button-text-color': theme.palette.common.white,
                },
                [theme.getColorSchemeSelector('dark')]: {
                  '--Button-background': theme.palette.common.black,
                  '--Button-text-color': theme.palette.common.white,
                },
              }),
            }),

          // Sets the styles for the "gradient secondary" button
          ...(ownerState.color === 'gradient-secondary' &&
            !ownerState.disabled && {
              '--Button-background': theme.palette.common.white,
              '--Button-text-color': theme.palette.common.black,
              color: 'var(--Button-text-color)',
              background: theme.vars.palette.gradient.secondary,
              backgroundSize: '100% 100%',
              backgroundPosition: '0% 0%',
              '&:hover': {
                backgroundSize: '400% 400%',
                backgroundPosition: '50% 40%',
              },

              // Outlined styles
              ...(ownerState.variant === 'outlined' && {
                isolation: 'isolate',
                color: 'var(--Button-text-color)',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  zIndex: -10,
                  margin: 'var(--variant-borderWidth)',
                  borderRadius:
                    toCustomProps(ownerState).shape === 'pill'
                      ? 999
                      : 'calc(var(--Button-radius) - var(--variant-borderWidth))',
                  border: 'var(--variant-borderWidth) solid transparent',
                  backgroundColor: 'var(--Button-background)',
                },
                '&:hover::after': {
                  backgroundColor: 'transparent',
                },
                [theme.getColorSchemeSelector('dark')]: {
                  '--Button-background': theme.palette.common.black,
                  '--Button-text-color': theme.palette.common.white,
                  '&:hover': {
                    '--Button-text-color': theme.palette.common.black,
                  },
                },
              }),
            }),
        };
      },
    },
  },
};

function Spinner() {
  return (
    <svg
      className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox='0 0 24 24'
    >
      <circle
        className='opacity-25'
        cx='12'
        cy='12'
        r='10'
        stroke='currentColor'
        strokeWidth='4'
      ></circle>
      <path
        className='opacity-75'
        fill='currentColor'
        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
      ></path>
    </svg>
  );
}

type OwnProps = {
  shape?: (typeof SHAPE)[number];
};

const Button = forwardRef(function Button(
  props:
    | JoyButtonProps
    | (Omit<LinkProps, 'color'> & OwnProps & Pick<JoyButtonProps, 'size'>),
  ref: React.ForwardedRef<HTMLElement>,
) {
  if ('href' in props) {
    const {
      href,
      replace,
      reloadDocument,
      state,
      preventScrollReset,
      relative,
      slotProps,
      ...linkProps
    } = props;
    return (
      <JoyButton
        component={Link}
        {...linkProps}
        href={href}
        replace={replace}
        state={state}
        preventScrollReset={preventScrollReset}
        relative={relative}
        reloadDocument={reloadDocument}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      />
    );
  }

  const { loading, ...buttonProps } = props;

  return (
    <JoyButton
      ref={ref as React.ForwardedRef<HTMLButtonElement>}
      {...buttonProps}
      startDecorator={loading ? <Spinner /> : buttonProps.startDecorator}
      disabled={loading || buttonProps.disabled}
    >
      <span>{props.children}</span>
    </JoyButton>
  );
});
Button.displayName = 'Button';

export { Button };

export const TextButton = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof Button>
>(function TextButton(props, ref) {
  const defaultColor = 'primary' as Exclude<
    ComponentPropsWithoutRef<typeof JoyButton>['color'],
    undefined
  >;
  const color = 'color' in props ? props.color ?? defaultColor : defaultColor;

  return (
    <Button
      {...mergeProps(props, {
        sx: {
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
          height: 'auto',
          minHeight: 'auto',
          ':hover': {
            backgroundColor: 'transparent',
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            color: (theme) => theme.palette[color][700],
          },
        } as ComponentPropsWithoutRef<typeof Button>['sx'],
      })}
      variant='plain'
      ref={ref}
    />
  );
});
